import { useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';

type PendingAutocountComponent = { name: string; color: string; isLighting?: boolean } | null;

type CountComponent = {
  color: string;
  name: string;
  symbol: string;
  referenceImage: string;
  symbolType?: string;
  componentType?: string;
  subscript?: string;
  description?: string;
};

type StartCountingComponents = (components: CountComponent[], options?: { parallel?: boolean }) => void;

interface UseAutocountFlowParams {
  newComponentName: string;
  newComponentColor: string;
  electricalKey: any;
  isAddingNewOnly: boolean;
  selectedComponentsForBatch: Set<string>;
  referenceAreaImage: string | null;
  pendingAutocountComponent: PendingAutocountComponent;
  pdfDocument: any;
  numPages: number | null;
  onAddComponentToKey?: (component: { name: string; color: string; symbol: string; referenceImage?: string }) => Promise<void>;
  onSwitchToCountingTab?: () => void;
  setIsAddingComponent: Dispatch<SetStateAction<boolean>>;
  setShowNewComponentModal: Dispatch<SetStateAction<boolean>>;
  setNewComponentName: Dispatch<SetStateAction<string>>;
  setNewComponentColor: Dispatch<SetStateAction<string>>;
  setPendingAutocountComponent: Dispatch<SetStateAction<PendingAutocountComponent>>;
  setIsAddingNewOnlyScanMode: Dispatch<SetStateAction<boolean>>;
  setAutocountMode: Dispatch<SetStateAction<'area-selection' | null>>;
  setAutocountSelection: Dispatch<SetStateAction<{ startX: number; startY: number; endX: number; endY: number } | null>>;
  setReferenceAreaImage: Dispatch<SetStateAction<string | null>>;
  setQuickCountSearch: Dispatch<SetStateAction<string>>;
  setShowAutocountConfirmModal: Dispatch<SetStateAction<boolean>>;
  setSelectedComponentsForBatch: Dispatch<SetStateAction<Set<string>>>;
  setSelectedDetectionsToKeep: Dispatch<SetStateAction<Set<string>>>;
  quickCountTriggerRef: { current: boolean };
  startCountingComponents: StartCountingComponents;
}

export function useAutocountFlow({
  newComponentName,
  newComponentColor,
  electricalKey,
  isAddingNewOnly,
  selectedComponentsForBatch,
  referenceAreaImage,
  pendingAutocountComponent,
  pdfDocument,
  numPages,
  onAddComponentToKey,
  onSwitchToCountingTab,
  setIsAddingComponent,
  setShowNewComponentModal,
  setNewComponentName,
  setNewComponentColor,
  setPendingAutocountComponent,
  setIsAddingNewOnlyScanMode,
  setAutocountMode,
  setAutocountSelection,
  setReferenceAreaImage,
  setQuickCountSearch,
  setShowAutocountConfirmModal,
  setSelectedComponentsForBatch,
  setSelectedDetectionsToKeep,
  quickCountTriggerRef,
  startCountingComponents,
}: UseAutocountFlowParams) {
  const handleStartQuickCount = useCallback(async () => {
    if (!newComponentName.trim()) return;

    setIsAddingComponent(true);

    try {
      const newComponent = {
        name: newComponentName.trim().toLowerCase(),
        color: newComponentColor,
        symbol: newComponentName.trim(),
      };

      if (onAddComponentToKey) {
        await onAddComponentToKey(newComponent);
      }

      if (onSwitchToCountingTab) {
        onSwitchToCountingTab();
      }

      setShowNewComponentModal(false);
      setNewComponentName('');
      setNewComponentColor('#3b82f6');
    } catch (error) {
      console.error('Failed to start quick count:', error);
    } finally {
      setIsAddingComponent(false);
    }
  }, [
    newComponentName,
    newComponentColor,
    onAddComponentToKey,
    onSwitchToCountingTab,
    setIsAddingComponent,
    setNewComponentColor,
    setNewComponentName,
    setShowNewComponentModal,
  ]);

  const handleStartAreaSelection = useCallback(() => {
    if (!newComponentName.trim()) return;

    const normalizedName = newComponentName.trim().toLowerCase();
    const comp = electricalKey?.components?.find(
      (c: any) =>
        (c.name || '').toLowerCase() === normalizedName ||
        (c.symbolType === 'Lighting' && (c.symbol || c.name || '').toLowerCase() === normalizedName)
    );
    const isLighting = comp?.symbolType === 'Lighting';

    setPendingAutocountComponent({
      name: normalizedName,
      color: newComponentColor,
      isLighting,
    });

    setShowNewComponentModal(false);

    if (isAddingNewOnly) {
      setIsAddingNewOnlyScanMode(true);
    }
    setAutocountMode('area-selection');
    setAutocountSelection(null);
    setReferenceAreaImage(null);
  }, [
    electricalKey,
    isAddingNewOnly,
    newComponentColor,
    newComponentName,
    setAutocountMode,
    setAutocountSelection,
    setIsAddingNewOnlyScanMode,
    setPendingAutocountComponent,
    setReferenceAreaImage,
    setShowNewComponentModal,
  ]);

  const handleRetakeAreaSelection = useCallback(() => {
    setShowNewComponentModal(false);
    if (isAddingNewOnly) {
      setIsAddingNewOnlyScanMode(true);
    }
    setAutocountMode('area-selection');
    setAutocountSelection(null);
    setReferenceAreaImage(null);
  }, [isAddingNewOnly, setAutocountMode, setAutocountSelection, setIsAddingNewOnlyScanMode, setReferenceAreaImage, setShowNewComponentModal]);

  const handleQuickAutocountFromReference = useCallback(
    (componentName: string, componentColor: string, referenceImage: string, isLighting?: boolean) => {
      setShowNewComponentModal(false);
      setQuickCountSearch('');
      quickCountTriggerRef.current = true;
      setPendingAutocountComponent({
        name: componentName,
        color: componentColor,
        isLighting: isLighting ?? false,
      });
      setReferenceAreaImage(referenceImage);
    },
    [quickCountTriggerRef, setPendingAutocountComponent, setQuickCountSearch, setReferenceAreaImage, setShowNewComponentModal]
  );

  const handleConfirmAutocount = useCallback(() => {
    if (!referenceAreaImage || !pendingAutocountComponent || !pdfDocument || !numPages) return;

    setShowAutocountConfirmModal(false);
    setAutocountMode(null);

    startCountingComponents([
      {
        color: pendingAutocountComponent.color,
        name: pendingAutocountComponent.name,
        symbol: pendingAutocountComponent.name,
        referenceImage: referenceAreaImage,
      },
    ]);
  }, [
    numPages,
    pdfDocument,
    pendingAutocountComponent,
    referenceAreaImage,
    setAutocountMode,
    setShowAutocountConfirmModal,
    startCountingComponents,
  ]);

  const handleBatchAutocount = useCallback(() => {
    if (!electricalKey?.components || !pdfDocument || !numPages) return;

    const selectedComponents = electricalKey.components.filter((c: any) => {
      const key = c.symbolType === 'Lighting' ? c.symbol || c.name : c.name;
      return selectedComponentsForBatch.has(key) && (c.referenceImage || c.symbolType === 'Lighting' || c.componentType === 'Switches');
    });

    if (selectedComponents.length === 0) {
      console.warn('No components selected');
      return;
    }

    setShowNewComponentModal(false);
    setQuickCountSearch('');
    setSelectedComponentsForBatch(new Set());

    startCountingComponents(
      selectedComponents.map((c: any) => ({
        color: c.color,
        name: c.name,
        symbol: c.symbol || c.name,
        referenceImage: c.referenceImage || '',
        symbolType: c.symbolType,
        componentType: c.componentType,
        subscript: c.subscript,
        description: c.description,
      }))
    );
  }, [
    electricalKey,
    numPages,
    pdfDocument,
    selectedComponentsForBatch,
    setQuickCountSearch,
    setSelectedComponentsForBatch,
    setShowNewComponentModal,
    startCountingComponents,
  ]);

  const toggleDetectionSelection = useCallback(
    (detectionId: string) => {
      setSelectedDetectionsToKeep((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(detectionId)) {
          newSet.delete(detectionId);
        } else {
          newSet.add(detectionId);
        }
        return newSet;
      });
    },
    [setSelectedDetectionsToKeep]
  );

  return {
    handleStartQuickCount,
    handleStartAreaSelection,
    handleRetakeAreaSelection,
    handleQuickAutocountFromReference,
    handleConfirmAutocount,
    handleBatchAutocount,
    toggleDetectionSelection,
  };
}
